// src/components/foam/FoamMyQuiver.js
//
// Screen 4 · My Quiver (signed-in customer's board list)
// Doc: handoff README "Screen 4"
//
// Phase 1: visually complete, displays existing data via the same
// userRegistrations array fetched in the existing flow.
// Phase 4: this becomes the default landing for returning customers.

import React, { useState, useRef, useEffect } from 'react';
import { FPage, FHeader, FLabel, FBrandText, FHeadline } from './FoamPrimitives';

/**
 * Initial avatar + account dropdown (sign out lives here — Nigel, 2026-06-05).
 * Tap the avatar → small menu with the signed-in email + Sign out.
 */
const AvatarMenu = ({ user, onSignOut }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const initial = (user?.displayName || user?.email || '?').charAt(0).toUpperCase();

  // Close on tap outside
  useEffect(() => {
    if (!open) return;
    const close = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('pointerdown', close);
    return () => document.removeEventListener('pointerdown', close);
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Account menu"
        onClick={() => setOpen(!open)}
        className="
          w-[34px] h-[34px] flex items-center justify-center
          font-sans font-bold text-[14px]
        "
        style={{
          border: '1.5px solid var(--foam-ink)',
          background: open ? 'var(--foam-ink)' : 'transparent',
          color: open ? 'var(--foam-paper)' : 'var(--foam-ink)',
          borderRadius: 0,
        }}
      >
        {initial}
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 z-50 flex flex-col"
          style={{
            top: 'calc(100% + 8px)',
            minWidth: 220,
            background: 'var(--foam-paper)',
            border: '1.5px solid var(--foam-ink)',
            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
          }}
        >
          <div
            className="px-[14px] py-[12px] font-mono text-[12px] tracking-[0.08em] text-foam-ink-2"
            style={{ borderBottom: '1px solid var(--foam-hair)', overflowWrap: 'anywhere' }}
          >
            {user?.email || 'Signed in'}
          </div>
          <button
            type="button"
            role="menuitem"
            onClick={() => { setOpen(false); onSignOut?.(); }}
            className="px-[14px] py-[14px] text-left font-sans font-bold text-[13px] uppercase text-foam-ink"
            style={{ letterSpacing: '0.12em', background: 'transparent' }}
          >
            Sign out
          </button>
        </div>
      )}
    </div>
  );
};

const FoamMyQuiver = ({ user, registrations = [], onSelect, onAddAnother, onSignOut }) => {
  return (
    <FPage>
      <FHeader right={<AvatarMenu user={user} onSignOut={onSignOut} />} />

      <FLabel>{user?.displayName || user?.email || 'Signed in'}</FLabel>

      {/* Design system: Saira Condensed 800, 72px, uppercase */}
      <FHeadline size={72} lineHeight={0.9}>
        My
        <FHeadline.Accent block>Quiver.</FHeadline.Accent>
      </FHeadline>

      <FLabel>
        {registrations.length} board{registrations.length === 1 ? '' : 's'} on file
      </FLabel>

      <div
        className="flex flex-col"
        style={{ border: '1.5px solid var(--foam-line)' }}
      >
        {registrations.length === 0 && (
          <div className="p-[18px] text-[14px] text-foam-ink-2 text-center">
            No boards registered yet. Tap "Register another board" below.
          </div>
        )}
        {registrations.map((reg, i) => {
          const isActive = i === 0; // visual emphasis on the most-recent for now
          const status = reg.verified ? 'Verified' : 'Pending';
          const isFindable = reg.findability?.enabled !== false; // legacy rows default to findable
          const statusLabel = isFindable ? `Findable · ${reg.serial || reg.sku || ''}` : `${status} · ${reg.serial || reg.sku || ''}`;
          return (
            <button
              key={reg.id || reg.sku}
              type="button"
              onClick={() => onSelect?.(reg)}
              className="text-left flex items-center gap-[14px] p-[14px]"
              style={{
                background: isActive ? '#FFF7F3' : 'var(--foam-paper)',
                borderBottom: i < registrations.length - 1 ? '1px solid var(--foam-hair)' : 'none',
                color: 'var(--foam-ink)',
              }}
            >
              {reg.productImage && (
                <img
                  src={reg.productImage}
                  alt=""
                  style={{ width: 56, height: 56, objectFit: 'contain' }}
                />
              )}
              <div className="flex-1 min-w-0">
                <FBrandText size={18} color="var(--foam-ink)" className="block" style={{ letterSpacing: '0.04em' }}>
                  {(reg.productName || 'BOARD').toUpperCase()} {reg.size || ''}
                </FBrandText>
                <div className="font-mono text-[12px] tracking-[0.1em] uppercase text-foam-ink-2 mt-[4px]">
                  {statusLabel}
                </div>
              </div>
              {isActive && (
                <span
                  className="bg-foam-orange"
                  style={{ width: 6, height: 6, borderRadius: '50%' }}
                />
              )}
              <span className="text-foam-ink text-[16px]">→</span>
            </button>
          );
        })}
      </div>

      <div className="flex-1" />

      <button
        type="button"
        onClick={onAddAnother}
        className="
          h-[60px] w-full
          font-sans font-semibold text-[15px]
          flex items-center justify-center
          text-foam-ink
        "
        style={{
          border: '1.5px dashed var(--foam-line)',
          background: 'transparent',
          borderRadius: 0, // square everywhere — locked brand decision
        }}
      >
        + Register another board
      </button>

    </FPage>
  );
};

export default FoamMyQuiver;
